import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { logger } from "@/modules/logger";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { useAppStore } from "@/store/utils/hooks";

import { AppleProactiveUpgradeModalView } from "./apple-proactive-upgrade-modal-view";
import { startProactiveCheckout } from "./proactive-checkout";

interface AppleProactiveUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutResetKey: number;
  checkoutIsExternallyPending?: boolean;
}

export const AppleProactiveUpgradeModal = observer(
  ({
    isOpen,
    onClose,
    checkoutResetKey,
    checkoutIsExternallyPending = false,
  }: AppleProactiveUpgradeModalProps) => {
    const { store } = useAppStore();
    const location = useLocation();
    const [isSubscribing, setIsSubscribing] = useState(false);
    const checkoutIsPending = isSubscribing || checkoutIsExternallyPending;
    const canSubscribe = store.subscriptions.appleProactiveUpgradeCanSubscribe;
    const proactivePricingAssignment = store.subscriptions.proactivePricingAssignment;
    const proactivePricingIsLoading = store.subscriptions.proactivePricingAssignmentIsLoading;
    const proactivePricingError = store.subscriptions.proactivePricingAssignmentError;
    const checkoutCanStart = Boolean(proactivePricingAssignment) || Boolean(proactivePricingError);

    useEffect(() => {
      setIsSubscribing(false);
    }, [checkoutResetKey]);

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      let canceled = false;

      store.subscriptions
        .fetchProactivePricingAssignment()
        .catch(() => undefined)
        .finally(() => {
          if (!canceled) {
            trackEvent(TrackedEvent.PaywallView, buildProactivePaywallEventPayload({ store }));
          }
        });

      return () => {
        canceled = true;
      };
    }, [isOpen, store]);

    const retryPricingAssignment = async () => {
      try {
        await store.subscriptions.fetchProactivePricingAssignment();
      } catch {
        // The visible error state is owned by the subscription store.
      }
    };

    const startSubscribe = async () => {
      trackEvent(TrackedEvent.PaywallUpgradeClick, buildProactivePaywallEventPayload({ store }));
      setIsSubscribing(true);
      let didStartExternalNavigation = false;

      try {
        const checkoutSession = await startProactiveCheckout({
          location,
          reminderShillUpsellAttributionContext:
            store.settingsModal.reminderShillUpsellAttributionContext,
          store,
        });

        didStartExternalNavigation = checkoutSession.didStartExternalNavigation;
        if (!didStartExternalNavigation) {
          onClose();
        }
      } catch (error) {
        trackEvent(TrackedEvent.PaywallUpgradeFailed, {
          ...buildProactivePaywallEventPayload({ store }),
          failure_stage: "checkout_start",
          failure_reason: "checkout_start_error",
          error_message: error instanceof Error ? error.message : "Unknown checkout error",
        });
        logger.error({
          message: "[AppleProactiveUpgradeModal] Failed to start proactive checkout",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
        toastModule.triggerToast({
          content:
            error instanceof Error
              ? error.message
              : "Failed to start Proactive checkout. Please try again.",
        });
      } finally {
        if (!didStartExternalNavigation) {
          setIsSubscribing(false);
        }
      }
    };

    const handleSubscribe = async () => {
      if (checkoutIsPending || !canSubscribe || !checkoutCanStart) {
        return;
      }

      await startSubscribe();
    };

    const handleSubscribeLater = async () => {
      if (checkoutIsPending || !checkoutCanStart) {
        return;
      }

      await startSubscribe();
    };

    return (
      <AppleProactiveUpgradeModalView
        isOpen={isOpen}
        onClose={onClose}
        onSubscribe={handleSubscribe}
        onSubscribeLater={handleSubscribeLater}
        onRetryPricing={retryPricingAssignment}
        canSubscribe={canSubscribe && checkoutCanStart}
        canSubscribeLater={checkoutCanStart}
        pricingIsUnavailable={Boolean(proactivePricingError) && !proactivePricingAssignment}
        isSubscribing={
          checkoutIsPending || (proactivePricingIsLoading && !proactivePricingAssignment)
        }
      />
    );
  }
);
